<template>
  <!--
    Room-type picker — a from-scratch dropdown that lets us surface the
    per-option roomDesc on hover (native <select><option> can't fire hover in
    any browser). Mirrors the mechanism in the new agent portal
    (fc-domestic-agent-pnim `wireRoomDropdown` / `showRoomDescPopup`):
    trigger + custom menu of <button role="option"> items + a single shared
    tooltip pinned to <body>.
  -->
  <div class="room-type-select" :class="{ 'is-open': open }">
    <!--
      Hint shown only when at least one option carries a description — so a
      row where nothing has extra text doesn't advertise a phantom feature.
      Small, subtle, above the trigger so it doesn't compete with the price.
      Wording adapts to mobile (tap) vs desktop (hover).
    -->
    <small v-if="hasAnyDesc" class="room-type-select__hint">
      {{ isMobile ? 'הקישו על חדר לתיאור' : 'ריחוף לתיאור החדר' }}
    </small>
    <button
      ref="trigger"
      type="button"
      class="room-type-select__trigger"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="listbox"
      :disabled="!options || options.length === 0"
      @click="toggle"
      @keydown="onTriggerKeydown"
      @mouseenter="isMobile ? null : onTriggerHover()"
      @mouseleave="isMobile ? null : hidePopup()"
    >
      <span class="room-type-select__label">{{ triggerLabel }}</span>
      <span class="room-type-select__caret" aria-hidden="true">▾</span>
    </button>
    <div
      v-if="open"
      ref="menu"
      class="room-type-select__menu"
      role="listbox"
      dir="rtl"
      :style="menuStyle"
      @mouseleave="hidePopup"
    >
      <button
        v-for="(opt, idx) in options"
        :key="opt.value + '-' + idx"
        type="button"
        role="option"
        class="room-type-select__item"
        :class="{
          'is-active': idx === activeIdx,
          'is-selected': opt.value === value,
        }"
        :aria-selected="opt.value === value ? 'true' : 'false'"
        @mouseenter="isMobile ? null : activate(idx, $event.currentTarget)"
        @click="onItemClick(idx, opt.value)"
      >
        {{ opt.text }}
      </button>
    </div>
  </div>
</template>

<script>
// Shared body-level tooltip element. All RoomTypeSelect instances share
// this one node — created lazily, hidden by default. Behavior mirrors
// showRoomDescPopup / hideRoomDescPopup from the new portal.
const POPUP_ID = 'room-type-desc-pop';
function getPopup() {
  let el = document.getElementById(POPUP_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = POPUP_ID;
    el.className = 'room-type-desc-pop is-hidden';
    el.setAttribute('role', 'tooltip');
    el.dir = 'rtl';
    document.body.appendChild(el);
  }
  return el;
}
function positionPopup(anchor) {
  const pop = getPopup();
  const r = anchor.getBoundingClientRect();
  // Un-hide first — width/height read 0 while display:none.
  pop.classList.remove('is-hidden');
  const w = pop.offsetWidth;
  const h = pop.offsetHeight;
  const gap = 12;
  // Prefer the left side (free space in this RTL layout); flip right if it
  // would overflow, then clamp to the viewport.
  let left = r.left - w - gap;
  if (left < 8) left = Math.min(r.right + gap, window.innerWidth - w - 8);
  if (left < 8) left = 8;
  const top = Math.max(8, Math.min(r.top, window.innerHeight - h - 8));
  pop.style.top = `${top}px`;
  pop.style.left = `${left}px`;
}
function showPopup(anchor, desc, heading = 'תיאור החדר', opts = {}) {
  const body = (desc || '').trim();
  if (body === '') return; // Nothing to say ⇒ don't open a blank box.
  const pop = getPopup();
  pop.textContent = '';
  const isMobile = opts && opts.mobile;
  // Mobile: centered modal-ish popup with a visible X. Desktop: side card,
  // no X (hover-driven, hides on mouseleave).
  pop.classList.toggle('is-mobile', !!isMobile);
  const title = document.createElement('strong');
  title.className = 'room-type-desc-pop__title';
  title.textContent = heading;
  pop.appendChild(title);
  pop.appendChild(document.createTextNode(body));
  if (isMobile) {
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'room-type-desc-pop__close';
    closeBtn.setAttribute('aria-label', 'סגור');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', hidePopupNow);
    pop.appendChild(closeBtn);
    // Mobile: center of viewport. Reveal first so classes apply.
    pop.classList.remove('is-hidden');
    pop.style.top = '';
    pop.style.left = '';
  } else {
    positionPopup(anchor);
  }
}
function hidePopupNow() {
  const el = document.getElementById(POPUP_ID);
  if (el) {
    el.classList.add('is-hidden');
    el.classList.remove('is-mobile');
  }
}

export default {
  name: 'RoomTypeSelect',
  props: {
    // v-model target — the currently-selected room code.
    value: { type: String, default: '' },
    // [{ value, text, desc }]; desc may be empty (row still renders, tooltip skipped).
    options: { type: Array, default: () => [] },
  },
  data() {
    return {
      open: false,
      activeIdx: -1,
      menuStyle: {}, // populated on open() with computed position
    };
  },
  computed: {
    triggerLabel() {
      const cur = (this.options || []).find((o) => o.value === this.value);
      return cur ? cur.text : (this.options && this.options.length ? this.options[0].text : '—');
    },
    selectedDesc() {
      const cur = (this.options || []).find((o) => o.value === this.value);
      return cur && typeof cur.desc === 'string' ? cur.desc : '';
    },
    // Hint should only appear when hovering can actually surface something.
    hasAnyDesc() {
      return (this.options || []).some((o) => o && typeof o.desc === 'string' && o.desc.trim() !== '');
    },
    isMobile() {
      return this.$store && this.$store.state && this.$store.state.device === 'mobile';
    },
  },
  methods: {
    toggle() {
      if (this.open) this.close();
      else this.openMenu();
    },
    openMenu() {
      if (!this.options || this.options.length === 0) return;
      this.open = true;
      this.activeIdx = Math.max(0, (this.options || []).findIndex((o) => o.value === this.value));
      // Position on next tick so menu offsetWidth is defined.
      this.$nextTick(() => this.positionMenu());
    },
    close() {
      this.open = false;
      this.activeIdx = -1;
      hidePopupNow();
    },
    choose(v) {
      this.$emit('input', v);
      this.close();
      // Keep focus on the trigger for keyboard flow parity with native select.
      if (this.$refs.trigger) this.$refs.trigger.focus();
    },
    activate(idx, itemEl) {
      this.activeIdx = idx;
      const opt = this.options[idx];
      if (opt && itemEl) showPopup(itemEl, opt.desc);
    },
    // Every tap on an option commits the selection — same as desktop's click.
    // If the option has a description, we ALSO open the mobile popup so the
    // user sees "you picked X, here's what X is". The earlier double-tap
    // flow ("preview then commit") caused users to think room changes did
    // nothing when they only tapped once — the price never updated.
    onItemClick(idx, value) {
      const opt = this.options[idx];
      const desc = opt && typeof opt.desc === 'string' ? opt.desc.trim() : '';
      if (this.isMobile && desc !== '') {
        // Emit the selection so v-model updates and the parent's roomKind
        // watcher fires (recomputing price, basis, etc.). Then close the
        // menu and pin the description popup against the trigger — which
        // is still mounted after close() (only the menu is v-if=open).
        this.$emit('input', value);
        this.close();
        this.$nextTick(() => {
          if (this.$refs.trigger) {
            showPopup(this.$refs.trigger, desc, 'תיאור החדר', { mobile: true });
          }
        });
      } else {
        this.choose(value);
      }
    },
    onTriggerHover() {
      // While closed, hovering the trigger surfaces the currently-selected room's
      // description — matches the new portal's behavior when the menu is idle.
      if (!this.open && this.$refs.trigger) showPopup(this.$refs.trigger, this.selectedDesc);
    },
    hidePopup() {
      hidePopupNow();
    },
    onTriggerKeydown(ev) {
      if (ev.key === 'Escape' && this.open) { this.close(); return; }
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        if (this.open && this.activeIdx >= 0) {
          this.choose(this.options[this.activeIdx].value);
        } else {
          this.openMenu();
        }
        return;
      }
      if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
      ev.preventDefault();
      if (!this.open) { this.openMenu(); return; }
      const last = this.options.length - 1;
      const next = ev.key === 'ArrowDown'
        ? Math.min(last, this.activeIdx + 1)
        : Math.max(0, this.activeIdx - 1);
      this.activeIdx = next;
      // Reposition tooltip against the newly-active item.
      this.$nextTick(() => {
        const items = this.$refs.menu ? this.$refs.menu.querySelectorAll('.room-type-select__item') : [];
        if (items[next]) {
          items[next].scrollIntoView({ block: 'nearest' });
          showPopup(items[next], this.options[next].desc);
        }
      });
    },
    positionMenu() {
      const trigger = this.$refs.trigger;
      const menu = this.$refs.menu;
      if (!trigger || !menu) return;
      const r = trigger.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const h = menu.offsetHeight;
      // Flip up if there's no room below AND enough room above.
      const flipUp = below < h + 12 && r.top > h + 12;
      // Mobile trigger is inside a narrow column (col-4 / px-1), so matching
      // its width would wrap every room name to one letter per line
      // (Ofir 2026-08-16). Give the menu a comfortable width, then align it
      // to the trigger's right edge (RTL) and clamp inside the viewport.
      const viewportPad = 12;
      let width;
      let left;
      if (this.isMobile) {
        width = Math.min(360, window.innerWidth - viewportPad * 2);
        // Prefer aligning to trigger's RIGHT edge (start of text in RTL);
        // clamp if that would push us off either side of the viewport.
        left = r.right - width;
        if (left < viewportPad) left = viewportPad;
        if (left + width > window.innerWidth - viewportPad) {
          left = window.innerWidth - width - viewportPad;
        }
      } else {
        width = r.width;
        left = r.left;
      }
      this.menuStyle = {
        position: 'fixed',
        width: `${width}px`,
        left: `${left}px`,
        top: flipUp ? `${r.top - h - 4}px` : `${r.bottom + 4}px`,
        zIndex: 2000,
      };
    },
    onDocumentClick(ev) {
      if (!this.open) return;
      if (this.$el && !this.$el.contains(ev.target)) {
        // Menu is teleported to fixed position but still lives in this.$el via v-if;
        // clicks on menu items already invoke choose() and stopPropagation isn't
        // needed — just close if the click was truly outside our root.
        this.close();
      }
    },
    onWindowResize() {
      if (this.open) this.positionMenu();
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowResize, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowResize, true);
    // If this instance was the one showing the tooltip when it unmounts, hide it.
    hidePopupNow();
  },
};
</script>

<style scoped>
/* Trigger button styled to visually match <b-form-select> so the row layout
   isn't disturbed — same height, border, radius, font. */
.room-type-select {
  position: relative;
  width: 100%;
}
.room-type-select__hint {
  display: block;
  font-size: 11px;
  color: #1f4c96;
  line-height: 1.2;
  margin-bottom: 3px;
  text-align: right;
  direction: rtl;
  font-weight: 600;
  opacity: 0.85;
}
.room-type-select__trigger {
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  background: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  color: #495057;
  font-size: 1rem;
  line-height: 1.5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: right;
  direction: rtl;
}
.room-type-select__trigger:disabled {
  background: #e9ecef;
  cursor: not-allowed;
}
.room-type-select__trigger:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
.room-type-select.is-open .room-type-select__trigger {
  border-color: #80bdff;
}
.room-type-select__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.room-type-select__caret {
  color: #6c757d;
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
}

.room-type-select__menu {
  max-height: 320px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #dbe0e6;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(31, 76, 150, 0.15);
  padding: 4px 0;
}
.room-type-select__item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  text-align: right;
  font-size: 14px;
  color: #1a3a5c;
  cursor: pointer;
  direction: rtl;
}
.room-type-select__item:hover,
.room-type-select__item.is-active {
  background: #eef4fb;
}
.room-type-select__item.is-selected {
  font-weight: 700;
}
/* Mobile: bigger tap targets + no wrapping, so room names stay on one line
   even on narrow phones (was breaking one letter per line inside the
   trigger's tight column). */
@media (max-width: 767px) {
  .room-type-select__menu {
    max-height: 60vh;
    padding: 6px 0;
  }
  .room-type-select__item {
    padding: 12px 16px;
    font-size: 15px;
    white-space: normal;
    line-height: 1.35;
  }
}
</style>

<style>
/* Non-scoped — the tooltip lives directly under <body>, so scoped selectors
   from this component would never match it. */
.room-type-desc-pop {
  position: fixed;
  z-index: 3000;
  max-width: 340px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #dbe0e6;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(31, 76, 150, 0.18);
  color: #1a3a5c;
  font-size: 13px;
  line-height: 1.5;
  text-align: right;
  direction: rtl;
  pointer-events: none; /* never intercepts hover — the underlying option keeps focus */
  white-space: pre-line;
}
.room-type-desc-pop.is-hidden {
  display: none;
}
.room-type-desc-pop__title {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #1f4c96;
}
.room-type-desc-pop__close {
  position: absolute;
  top: 6px;
  left: 8px;
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #6b7d95;
  cursor: pointer;
  padding: 4px 8px;
  pointer-events: auto; /* override the parent's pointer-events:none */
}
.room-type-desc-pop__close:hover {
  color: #1a3a5c;
}
/* Mobile: the popup becomes a centered "modal card" (still under body, still
   :fixed) with the X visible top-left (RTL). Nearly full-viewport width so
   long room descriptions read as one block — Ofir 2026-08-16 wanted the
   narrow variant widened all the way. */
.room-type-desc-pop.is-mobile {
  position: fixed;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%);
  width: calc(100vw - 24px);
  max-width: calc(100vw - 24px);
  max-height: 80vh;
  overflow-y: auto;
  padding: 32px 18px 20px;
  pointer-events: auto;
  z-index: 4000;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  font-size: 15px;
  line-height: 1.55;
}
.room-type-desc-pop.is-mobile .room-type-desc-pop__title {
  font-size: 16px;
}
.room-type-desc-pop.is-mobile .room-type-desc-pop__close {
  font-size: 28px;
  padding: 6px 12px;
  top: 4px;
  left: 6px;
}
</style>
